import React from 'react';

type Props = {
    darkMode: boolean;
    onLoad: (url: string) => Promise<void>;
    onClose: () => void;
};

type State = {
    url: string;
    loading: boolean;
    error: string | null;
};

export default class LoadUrlModal extends React.Component<Props, State> {
    private inputRef = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            url: '',
            loading: false,
            error: null
        };
    }

    componentDidMount() {
        // Focus the input on mount
        this.inputRef.current?.focus();
    }

    handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ url: e.target.value, error: null });
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !this.state.loading && this.state.url.trim()) {
            this.handleLoad();
        } else if (e.key === 'Escape') {
            this.props.onClose();
        }
    };

    handleLoad = async () => {
        const { url } = this.state;
        if (!url.trim()) return;

        // Basic URL validation
        try {
            new URL(url);
        } catch {
            this.setState({ error: 'Please enter a valid URL' });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            await this.props.onLoad(url.trim());
            this.props.onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load ontology';
            this.setState({ error: message, loading: false });
        }
    };

    render() {
        const { onClose } = this.props;
        const { url, loading, error } = this.state;

        return (
            <div
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <div className="bg-white dark:bg-surface-dark-elevated text-slate-700 dark:text-white rounded-lg w-[90%] max-w-[600px] shadow-[0_8px_32px_rgba(100,120,140,0.2)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-accent-border">
                    {/* Header */}
                    <div className="p-4 border-b border-slate-200 dark:border-white/10">
                        <h3 className="m-0 text-slate-800 dark:text-white text-lg font-semibold">
                            Load Ontology from URL
                        </h3>
                        <p className="mt-1 text-xs text-slate-500 dark:text-white/60">
                            Enter a URL to load an OWL ontology (supports Turtle, RDF/XML, OWL/XML)
                        </p>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        <div className="mb-4">
                            <label className="block text-xs text-slate-500 dark:text-white/60 mb-1.5">
                                Ontology URL
                            </label>
                            <input
                                ref={this.inputRef}
                                type="url"
                                value={url}
                                onChange={this.handleUrlChange}
                                onKeyDown={this.handleKeyDown}
                                placeholder="https://example.org/ontology.rdf"
                                disabled={loading}
                                className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-accent/50 bg-white dark:bg-[#202030] text-slate-700 dark:text-white placeholder-slate-400 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent disabled:opacity-50"
                            />
                        </div>

                        {error && (
                            <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30">
                                <p className="text-xs text-red-600 dark:text-red-400 m-0">
                                    {error}
                                </p>
                            </div>
                        )}

                        <div className="text-xs text-slate-500 dark:text-white/50">
                            <p className="m-0 mb-1">Note: Some servers may block cross-origin requests (CORS).</p>
                            <p className="m-0">Imported ontologies (owl:imports) will be loaded automatically.</p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-200 dark:border-white/10 flex justify-end gap-2">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-accent/50 text-slate-600 dark:text-white/80 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={this.handleLoad}
                            disabled={loading || !url.trim()}
                            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Loading...
                                </>
                            ) : (
                                'Load'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
